// Types for the QualiCheck AI API (checklist-be: AiAnalysisController, FindingController, CapaController, ...).

export type Permission =
  | "AI_ANALYSE"
  | "AI_REVIEW"
  | "CAPA_MANAGE"
  | "CAPA_VALIDATE_ROOT_CAUSE"
  | "CAPA_VERIFY_EFFECTIVENESS"
  | "CAPA_CLOSE"
  | "REPORT_APPROVE"
  | "AUDIT_VIEW";

export const ALL_PERMISSIONS: Permission[] = [
  "AI_ANALYSE",
  "AI_REVIEW",
  "CAPA_MANAGE",
  "CAPA_VALIDATE_ROOT_CAUSE",
  "CAPA_VERIFY_EFFECTIVENESS",
  "CAPA_CLOSE",
  "REPORT_APPROVE",
  "AUDIT_VIEW",
];

export const CLASSIFICATIONS = [
  "CONFORMITY",
  "NOT_DEMONSTRATED",
  "OPPORTUNITY_FOR_IMPROVEMENT",
  "OBSERVATION",
  "MINOR",
  "MAJOR",
  "CRITICAL",
] as const;
export type Classification = (typeof CLASSIFICATIONS)[number];

export const EVIDENCE_STATUSES = ["OBSERVED", "DOCUMENTED", "REPORTED", "INFERRED", "NOT_DEMONSTRATED"] as const;
export type EvidenceStatus = (typeof EVIDENCE_STATUSES)[number];

export type RiskLevel = "LOW" | "MODERATE" | "HIGH" | "CRITICAL" | "NOT_DETERMINED";
export type FindingReviewStatus = "AI_PROPOSED" | "UNDER_REVIEW" | "ACCEPTED" | "MODIFIED" | "REJECTED" | "CLOSED";
export type FindingAction = "CLAIM" | "ACCEPT" | "REJECT" | "CLOSE" | "REOPEN";
export type AnalysisStatus = "QUEUED" | "PROCESSING" | "DRAFT_READY" | "FAILED" | "UNDER_REVIEW" | "REVIEWED" | "SUPERSEDED";
export const IMPACTS = ["NONE", "POTENTIAL", "ACTUAL"] as const;
export type ProductIntegrityImpact = (typeof IMPACTS)[number];

export type CapaStatus =
  | "AI_PROPOSED"
  | "UNDER_REVIEW"
  | "ACCEPTED"
  | "ASSIGNED"
  | "IN_PROGRESS"
  | "IMPLEMENTED"
  | "EFFECTIVENESS_VERIFICATION"
  | "EFFECTIVENESS_VERIFIED"
  | "CLOSED"
  | "REJECTED"
  | "REOPENED";
export const CAPA_FLOW: CapaStatus[] = [
  "AI_PROPOSED",
  "UNDER_REVIEW",
  "ACCEPTED",
  "ASSIGNED",
  "IN_PROGRESS",
  "IMPLEMENTED",
  "EFFECTIVENESS_VERIFICATION",
  "EFFECTIVENESS_VERIFIED",
  "CLOSED",
];
export type CapaAction =
  | "REVIEW"
  | "ACCEPT"
  | "ASSIGN"
  | "START"
  | "IMPLEMENT"
  | "VERIFY"
  | "RECORD_EFFECTIVENESS"
  | "CLOSE"
  | "REJECT"
  | "REOPEN";
export type EffectivenessResult = "EFFECTIVE" | "PARTIALLY_EFFECTIVE" | "NOT_EFFECTIVE";

export interface UserRef {
  id: number;
  name: string;
}

export interface ShipmentInfo {
  awb?: string | null;
  flightNo?: string | null;
  flightDate?: string | null;
  origin?: string | null;
  destination?: string | null;
  specialHandlingCodes?: string | null;
  cargoDescription?: string | null;
}

export interface ChecklistRef {
  id: number;
  referenceNo: string | null;
  title: string;
  site: string | null;
  process: string | null;
  operationalArea: string | null;
  shipment: ShipmentInfo | null;
  awbFromChecklistText?: boolean;
}

export interface EvidenceView {
  id: number;
  code: string | null;
  type: string;
  questionId: number | null;
  page: number | null;
  mime: string;
  name: string | null;
}

export interface FindingSummary {
  id: number;
  findingRef: string;
  tempId: string | null;
  statement: string;
  classification: Classification;
  evidenceStatus: EvidenceStatus;
  likelihood: number | null;
  severity: number | null;
  riskScore: number | null;
  riskLevel: RiskLevel;
  reviewStatus: FindingReviewStatus;
  isUrgent: boolean;
  capaRequired: boolean;
  analysisId: number;
  analysisRef: string | null;
  checklistRef: string | null;
  checklistTitle: string | null;
}

export interface QuestionAssessment {
  questionId: number;
  section: string | null;
  questionText: string | null;
  answer: string | null;
  reason: string | null;
  controlStatus: string;
  evidenceStatus: EvidenceStatus;
  rationale: string | null;
  evidenceRefs: string[];
  gaps: string[];
}

export interface AnalysisListItem {
  id: number;
  analysisRef: string;
  version: number;
  status: AnalysisStatus;
  hasUrgent: boolean;
  triggeredAt: string;
  completedAt: string | null;
  findingCount: number;
  reviewer: string | null;
  legacyAces: boolean;
}

export interface AnalysisRegisterItem extends AnalysisListItem {
  checklistId: number;
  checklistRef: string | null;
  checklistTitle: string | null;
  site: string | null;
}

export interface DashboardSummary {
  reportsSubmittedToday: number;
  reportsAwaitingReview: number | null;
  analysesAwaitingReview: number | null;
  openCriticalFindings: number | null;
  overdueCapas: number | null;
  capasAwaitingEffectiveness: number | null;
  alerts: { type: "FINDING" | "CAPA"; id: number; ref: string; text: string | null; daysOverdue: number | null }[];
  recentActivity: AuditEntry[] | null;
}

export interface AnalysisView {
  id: number;
  analysisRef: string;
  checklist: ChecklistRef;
  submissionId: number;
  version: number;
  parentAnalysisId: number | null;
  status: AnalysisStatus;
  language: string;
  modelId: string;
  instructionVersion: string | null;
  acesEdition: string | null;
  legacyAces: boolean;
  reviewedBy: UserRef | null;
  reviewedAt: string | null;
  executiveSummary: string | null;
  overallAssessment: { rating: string; narrative: string } | null;
  positivePractices: { text: string; question_ids: number[]; evidence_refs: string[] }[] | null;
  evidenceGaps: { text: string; question_ids: number[]; impact: string | null }[] | null;
  priorityActions: { text: string; finding_temp_ids: string[]; priority: string }[] | null;
  limitations: string[] | null;
  hasUrgent: boolean;
  triggeredBy: UserRef | null;
  triggeredAt: string;
  completedAt: string | null;
  regenerateReason: string | null;
  errorCode: string | null;
  attempts: number;
  tokensIn: number | null;
  tokensOut: number | null;
  latencyMs: number | null;
  questionAssessments: QuestionAssessment[];
  findings: FindingSummary[];
  evidence: EvidenceView[];
}

export interface PreAnalysisAnswer {
  questionId: number;
  section: "MANAGER" | "EMPLOYEE";
  position: number | null;
  title: string;
  requirementText: string | null;
  acesClauseId: number | null;
  answer: string | null;
  reason: string | null;
  note: string | null;
  confidence: "HIGH" | "LOW" | "NOT_FOUND" | null;
  evidenceIds: number[];
}

export interface PreAnalysisView {
  checklist: ChecklistRef;
  siteId: number | null;
  temperatureMin: number | null;
  temperatureMax: number | null;
  isTemperature: boolean | null;
  submittedAt: string | null;
  submittedBy: UserRef | null;
  submissionId: number | null;
  parseStatus: "PARSED" | "PARSED_WITH_WARNINGS" | "NEEDS_VISION" | "FAILED" | null;
  source: string | null;
  parseWarnings: string[];
  temperature: number | null;
  overallComment: string | null;
  signaturePresent: boolean;
  pdfUrl: string | null;
  answers: PreAnalysisAnswer[];
  evidence: EvidenceView[];
  missingContext: string[];
  analyses: AnalysisListItem[];
  aiEnabled: boolean;
  acesEdition: string | null;
}

export interface FindingRevision {
  id: number;
  field: string;
  aiOriginalValue: string | null;
  previousValue: string | null;
  newValue: string | null;
  userId: number;
  userName: string | null;
  reason: string;
  changedAt: string;
}

export interface StatusHistory<S> {
  id: number;
  fromStatus: S | null;
  toStatus: S;
  userId: number | null;
  userName: string | null;
  reason: string | null;
  changedAt: string;
}

export interface FindingEntity {
  id: number;
  findingRef: string;
  analysisId: number;
  checklistId: number;
  tempId: string | null;
  origin: string;
  process: string | null;
  cargoJourneyStageId: number | null;
  ccpId: number | null;
  acesClauseId: number | null;
  applicableRequirement: string | null;
  objectiveEvidence: string | null;
  evidenceStatus: EvidenceStatus;
  statement: string;
  classification: Classification;
  likelihood: number | null;
  likelihoodRationale: string | null;
  severity: number | null;
  severityRationale: string | null;
  riskScore: number | null;
  riskLevel: RiskLevel;
  productIntegrityImpact: ProductIntegrityImpact | null;
  productIntegrityDescription: string | null;
  immediateContainment: string | null;
  recommendedAction: string | null;
  capaRequired: boolean;
  reviewStatus: FindingReviewStatus;
  isUrgent: boolean;
  reviewedByUserId: number | null;
  reviewedAt: string | null;
  closedByUserId: number | null;
  closedAt: string | null;
  questionIds: number[];
  evidenceFileIds: number[];
  version: number;
}

export interface FindingDetail {
  finding: FindingEntity;
  analysisRef: string;
  analysisStatus: AnalysisStatus;
  legacyAces: boolean;
  checklist: ChecklistRef;
  aiOriginal: any;
  possibleCauses: { hypothesis: string; verification_needed: string }[] | null;
  capaProposal: Record<string, string | null> | null;
  confirmationsNeeded: string[] | null;
  stage: string | null;
  ccp: string | null;
  acesClause: string | null;
  questions: { questionId: number; section: string; title: string; answer: string | null; reason: string | null; evidenceIds: number[] }[];
  evidence: EvidenceView[];
  revisions: FindingRevision[];
  history: StatusHistory<FindingReviewStatus>[];
  comments: { id: number; userId: number; userName: string | null; text: string; createdAt: string }[];
  capas: { id: number; capaRef: string; status: CapaStatus }[];
  allowedActions: FindingAction[];
  canEdit: boolean;
  canCreateCapa: boolean;
  readyToClose: boolean;
}

export interface CapaEntity {
  id: number;
  capaRef: string;
  findingId: number;
  origin: "AI_PROPOSED" | "MANUAL";
  status: CapaStatus;
  immediateContainment: string | null;
  correction: string | null;
  possibleRootCause: string | null;
  validatedRootCause: string | null;
  rootCauseValidatedAt: string | null;
  correctiveAction: string | null;
  preventiveAction: string | null;
  responsibleFunction: string | null;
  responsiblePersonId: number | null;
  targetDate: string | null;
  implementationEvidenceText: string | null;
  effectivenessVerification: string | null;
  effectivenessResult: EffectivenessResult | null;
  effectivenessNotes: string | null;
  verifiedAt: string | null;
  closedAt: string | null;
  version: number;
  createdAt: string;
}

export interface CapaDetail {
  capa: CapaEntity;
  finding: FindingSummary;
  analysisId: number;
  checklist: ChecklistRef;
  aiOriginal: Record<string, string | null> | null;
  responsiblePerson: UserRef | null;
  rootCauseValidatedBy: UserRef | null;
  verifiedBy: UserRef | null;
  closedBy: UserRef | null;
  evidence: EvidenceView[];
  history: StatusHistory<CapaStatus>[];
  allowedActions: CapaAction[];
  canEdit: boolean;
  canValidateRootCause: boolean;
  overdue: boolean;
}

export interface CapaListItem {
  id: number;
  capaRef: string;
  status: CapaStatus;
  findingId: number;
  findingRef: string | null;
  classification: Classification | null;
  riskLevel: RiskLevel | null;
  isUrgent: boolean;
  responsibleFunction: string | null;
  responsiblePerson: UserRef | null;
  targetDate: string | null;
  overdue: boolean;
  checklistRef: string | null;
}

export interface ReportListItem {
  id: number;
  reportVersion: number;
  status: "DRAFT" | "FINAL";
  language: string;
  fileId: number;
  sha256: string;
  generatedBy: UserRef | null;
  generatedAt: string;
  approvedBy: UserRef | null;
  approvedAt: string | null;
}

export interface AuditEntry {
  id: number;
  entityType: string;
  entityId: number;
  entityRef: string | null;
  action: string;
  field: string | null;
  previousValue: string | null;
  newValue: string | null;
  userId: number | null;
  userName: string | null;
  reason: string | null;
  correlationId: string | null;
  createdAt: string;
}

export interface Site {
  id: number;
  name: string;
  code: string | null;
  airportIata: string | null;
  address: string | null;
  active: boolean;
}

export interface LovItem {
  id: number | null;
  value: string;
  source: "ACES" | "TENANT";
}

export interface AcesEdition {
  id: number;
  code: string;
  title: string;
  effectiveDate: string | null;
  status: "DRAFT" | "ACTIVE" | "RETIRED";
}

export interface AcesCatalogue {
  edition: AcesEdition;
  stages: { id: number; code: string; name: string; position: number }[];
  processes: { id: number; code: string; name: string }[];
  ccps: { id: number; code: string; name: string; stageId: number | null }[];
  clauses: {
    id: number;
    refCode: string;
    title: string;
    requirementSummary: string | null;
    processId: number | null;
    stageId: number | null;
    ccpId: number | null;
    active: boolean;
  }[];
}

export interface EffectiveAiSettings {
  tenantId: number;
  aiEnabled: boolean;
  acesEditionId: number | null;
  acesEditionCode: string | null;
  pinnedAcesEditionId: number | null;
  monthlyTokenBudget: number;
  maxImagesPerAnalysis: number;
}
